import {type FC, type ReactNode, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import classNames from 'classnames'
import {twMerge} from 'tailwind-merge'
import ReactMarkdown from 'react-markdown'

import {RenderIf} from 'components/Basic/RenderIf'
import {SparkleIcon} from 'components/Basic/SvgIcon'

const DOPAMINE_LOADING_PLACEHOLDER_TEXT =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris. Ut enim ad minim veniam.'

export const DOPAMINE_LOADING_MESSAGE_INTERVAL_MS = 2500

export const DOPAMINE_LOADING_MESSAGE_KEYS = [
  'AI_USAGE_DOPAMINE_LOADING_MESSAGE_1',
  'AI_USAGE_DOPAMINE_LOADING_MESSAGE_2',
  'AI_USAGE_DOPAMINE_LOADING_MESSAGE_3',
  'AI_USAGE_DOPAMINE_LOADING_MESSAGE_4',
  'AI_USAGE_DOPAMINE_LOADING_MESSAGE_5',
  'AI_USAGE_DOPAMINE_LOADING_MESSAGE_6',
  'AI_USAGE_DOPAMINE_LOADING_MESSAGE_7',
  'AI_USAGE_DOPAMINE_LOADING_MESSAGE_8',
] as const

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

const dopamineCardBackground = 'bg-white/[0.075]'
const dopamineCardShadow = 'shadow-[0_4px_6px_rgba(0,0,0,0.2)]'

const MARKDOWN_ALLOWED_ELEMENTS = [
  'h2',
  'h3',
  'p',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'code',
]

type MarkdownNodeProps = {children?: ReactNode}
type HeadingLevel = 'h2' | 'h3' | 'h4'

const MarkdownP = ({children}: MarkdownNodeProps) => (
  <p className="my-1 karla text-xs text-white/60">{children}</p>
)

const MarkdownUl = ({children}: MarkdownNodeProps) => (
  <ul className="list-disc list-outside ml-3 my-1.5 karla text-xs text-white/60">
    {children}
  </ul>
)

const MarkdownOl = ({children}: MarkdownNodeProps) => (
  <ol className="list-decimal list-outside ml-3 my-1.5 karla text-xs text-white/60">
    {children}
  </ol>
)

const MarkdownLi = ({children}: MarkdownNodeProps) => (
  <li className="mb-0.5 karla text-xs text-white/60">{children}</li>
)

const MarkdownStrong = ({children}: MarkdownNodeProps) => (
  <strong className="karla-bold text-white/60">{children}</strong>
)

const MarkdownEm = ({children}: MarkdownNodeProps) => (
  <em className="karla-exl text-white/60">{children}</em>
)

const MarkdownCode = ({children}: MarkdownNodeProps) => (
  <code className="px-1 py-0.5 rounded bg-white/10 karla text-xs text-white/60 font-normal">
    {children}
  </code>
)

const createMarkdownHeading = (HeadingTag: HeadingLevel) => {
  const MarkdownHeading = ({children}: MarkdownNodeProps) => {
    if (children?.toString().toLowerCase().trim() === 'dopamine') {
      return null
    }
    return (
      <HeadingTag className="text-xs mt-3 mb-1 abc-bold uppercase">
        {children}
      </HeadingTag>
    )
  }
  return MarkdownHeading
}

const createMarkdownComponents = (level: HeadingLevel) => {
  const heading = createMarkdownHeading(level)
  return {
    h2: heading,
    h3: heading,
    p: MarkdownP,
    ul: MarkdownUl,
    ol: MarkdownOl,
    li: MarkdownLi,
    strong: MarkdownStrong,
    em: MarkdownEm,
    code: MarkdownCode,
  }
}

const MARKDOWN_COMPONENTS_BY_LEVEL: Record<
  HeadingLevel,
  ReturnType<typeof createMarkdownComponents>
> = {
  h2: createMarkdownComponents('h2'),
  h3: createMarkdownComponents('h3'),
  h4: createMarkdownComponents('h4'),
}

export interface DopamineMarkdownCardProps {
  markdown?: string
  className?: string
  dataCy?: string
  titleDataCy?: string
  summaryDataCy?: string
  isLoading?: boolean
  titleLevel?: 'h2' | 'h3'
  markdownHeadingLevel?: HeadingLevel
}

const DopamineLoadingMessage: FC<{dataCy: string}> = ({dataCy}) => {
  const {t} = useTranslation(['aiUsage'])
  const [messageIndex, setMessageIndex] = useState(0)

  useEffect(() => {
    const prefersReducedMotion =
      window.matchMedia?.(REDUCED_MOTION_QUERY)?.matches ?? false
    if (prefersReducedMotion) return

    const intervalId = window.setInterval(() => {
      setMessageIndex(
        index => (index + 1) % DOPAMINE_LOADING_MESSAGE_KEYS.length,
      )
    }, DOPAMINE_LOADING_MESSAGE_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [])

  return (
    <span aria-hidden="true" className="flex items-center gap-1.5">
      <span
        className="flex shrink-0 [&_path]:[transform-box:fill-box] [&_path]:origin-center [&_path]:animate-dopamine-sparkle-twinkle [&_path:nth-child(2)]:[animation-delay:200ms] [&_path:nth-child(3)]:[animation-delay:400ms] motion-reduce:[&_path]:animate-none"
        data-cy={`${dataCy}-icon`}
      >
        <SparkleIcon width={16} height={16} classes="text-dope-purple-50" />
      </span>
      <span
        key={messageIndex}
        aria-hidden="true"
        className="karla-bold text-xs leading-4.5 text-transparent bg-clip-text bg-[length:200%_100%] bg-gradient-to-r from-white/60 via-white to-white/60 animate-dopamine-message motion-reduce:animate-none motion-reduce:text-white"
        data-cy={dataCy}
      >
        {t(`aiUsage:${DOPAMINE_LOADING_MESSAGE_KEYS[messageIndex]}`)}
      </span>
    </span>
  )
}

/**
 * Shared Dopamine markdown card. SSPM keeps the default selectors and heading
 * levels. AI Usage passes its own data-cy values, an h3 title, and h4 summary
 * headings, and can show the loading state.
 */
export const DopamineMarkdownCard: FC<DopamineMarkdownCardProps> = ({
  markdown = '',
  className,
  dataCy = 'sspm-dopamine-card',
  titleDataCy = 'dopamine-card-title',
  summaryDataCy = 'dopamine-card-summary',
  isLoading = false,
  titleLevel = 'h2',
  markdownHeadingLevel = 'h2',
}) => {
  const {t} = useTranslation(['aiUsage'])
  const TitleTag = titleLevel

  return (
    <div
      className={twMerge(
        classNames(
          'relative flex flex-col gap-1.5 text-sm text-white p-3 mr-4 rounded karla',
          dopamineCardBackground,
          dopamineCardShadow,
          'before:content-[""] before:absolute before:inset-0 before:rounded before:border-0.5 before:border-white/15',
          'before:bg-gradient-to-br before:from-dope-purple-500/40 before:via-dope-purple-400/30 before:to-dope-purple-500/40',
        ),
        className,
      )}
      data-cy={dataCy}
    >
      <TitleTag className="leading-3.5 z-1 text-white" data-cy={titleDataCy}>
        <span className="abc-bold">DOP</span>
        <span className="cositimes">AMINE</span>
      </TitleTag>

      <RenderIf isTrue={isLoading}>
        <div
          className="relative flex h-24 min-h-0 overflow-hidden"
          data-cy={`${dataCy}-loading`}
        >
          <p
            className="my-1 karla text-xs text-dope-purple-600 blur-sm pointer-events-none select-none leading-4.5 z-1"
            aria-hidden="true"
            data-cy={`${dataCy}-loading-placeholder`}
          >
            {DOPAMINE_LOADING_PLACEHOLDER_TEXT}
          </p>
          <div className="absolute inset-0 z-10 flex items-center justify-center text-center px-2">
            <output className="sr-only">
              {t('aiUsage:AI_USAGE_DOPAMINE_SUMMARY_LOADING')}
            </output>
            <DopamineLoadingMessage dataCy={`${dataCy}-loading-message`} />
          </div>
        </div>
      </RenderIf>

      <RenderIf isTrue={!isLoading}>
        <div
          className="karla text-xs font-normal leading-4.5 break-words text-white z-1"
          data-cy={summaryDataCy}
        >
          <ReactMarkdown
            allowedElements={MARKDOWN_ALLOWED_ELEMENTS}
            components={MARKDOWN_COMPONENTS_BY_LEVEL[markdownHeadingLevel]}
          >
            {markdown}
          </ReactMarkdown>
        </div>
      </RenderIf>
    </div>
  )
}
